// IndexedDB приложения (Dexie): очередь офлайн-мутаций, незавершённая тренировка, кэш TanStack Query.

import Dexie, { type EntityTable } from 'dexie'
import { getActiveUserId, registerIdentityStorage } from '@/api/identity'

export type MutationMethod = 'PUT' | 'POST' | 'DELETE'

/** Запрос, ждущий отправки. Порядок отправки — по `id` (автоинкремент). */
export type QueuedMutation = {
  readonly owner_id: string
  id?: number
  method: MutationMethod
  /** Полный путь с /api: '/api/sessions/<uuid>/sets/<uuid>'. */
  url: string
  body: unknown
  created_at: string
  attempts: number
  /** Текст последней временной ошибки (сеть, 5xx) — для подсказки. */
  last_error?: string | null
}

/** Мутация, отвергнутая сервером (4xx, кроме 401): из очереди вынесена, текст ошибки сохранён. */
export type FailedMutation = {
  readonly owner_id: string
  id?: number
  method: MutationMethod
  url: string
  body: unknown
  created_at: string
  failed_at: string
  status: number
  code: string
  message: string
}

export type ActiveSessionRow = { key: 'current'; data: unknown; updated_at: string }

export type CacheRow = { key: string; value: unknown }

export class OfflineDb extends Dexie {
  mutations!: EntityTable<QueuedMutation, 'id'>
  failed!: EntityTable<FailedMutation, 'id'>
  activeSession!: EntityTable<ActiveSessionRow, 'key'>
  cache!: EntityTable<CacheRow, 'key'>
  metadata!: EntityTable<CacheRow, 'key'>

  constructor(name = 'health-offline') {
    super(name)
    this.version(1).stores({
      mutations: '++id, url',
      failed: '++id',
      activeSession: 'key',
      cache: 'key',
    })
    this.version(2).stores({ metadata: 'key' })
  }
}

const databases = new Map<string, OfflineDb>()
export function ownerOfflineDb(owner: string): OfflineDb {
  let db = databases.get(owner)
  if (!db) { db = new OfflineDb(`health-offline-user-${owner}`); databases.set(owner, db) }
  return db
}
export function getOfflineDb(): OfflineDb {
  const owner = getActiveUserId()
  if (!owner) throw new Error('Подтверди вход для доступа к офлайн-данным')
  return ownerOfflineDb(owner)
}
// Compatibility facade for synchronous table users. Async operations capture getOfflineDb().
export const offlineDb = new Proxy({} as OfflineDb, {
  get(_target, key) {
    const db = getOfflineDb()
    const value = Reflect.get(db, key, db) as unknown
    return typeof value === 'function' ? value.bind(db) : value
  },
})
registerIdentityStorage(async (owner) => {
  if (!owner) return
  const db = ownerOfflineDb(owner)
  await db.transaction('rw', db.failed, db.cache, db.activeSession, db.mutations, async () => {
    await db.failed.clear()
    await db.cache.clear()
    const workout = await db.activeSession.get('current')
    if (!(workout?.data as { draft?: boolean } | undefined)?.draft && !(await db.mutations.count())) {
      await db.activeSession.clear()
    }
  })
}, async (user) => {
  if (!user.legacy_owner || !(await Dexie.exists('health-offline'))) return
  const legacy = new OfflineDb()
  const db = ownerOfflineDb(user.id)
  try {
    const mutations = await legacy.mutations.toArray()
    const workout = await legacy.activeSession.get('current')
    await db.transaction('rw', db.mutations, db.activeSession, db.metadata, async () => {
      if (await db.metadata.get('legacy-claimed')) return
      await db.mutations.bulkAdd(mutations.map(({ id: _id, ...row }) => ({ ...row, owner_id: user.id })))
      if (workout && !(await db.activeSession.get('current'))) await db.activeSession.put(workout)
      await db.metadata.put({ key: 'legacy-claimed', value: true })
    })
    await legacy.delete()
  } finally { legacy.close() }
})
